import {css} from 'styled-components';

import type {Size, StyledProps} from './types';

import {ms} from '../../styles/normalizeSize';
import {getColor, makeColorScheme} from '../../styles/utils';
import {platform} from '../../utils/platform';

export const cScheme = makeColorScheme<Required<StyledProps>>({
  danger: {
    active: 'button.fill3',
    disabled: 'button.fill5',
    hover: 'button.fill3',
    primary: 'button.fill3',
    secondary: 'error',
  },
  error: {
    // TODO: ask for the hover,active, styles
    active: 'error',
    disabled: 'lostColors.errorLight',
    hover: 'error',
    primary: 'error',
    secondary: 'text2',
  },
  gray: {
    active: 'button.fill3',
    disabled: 'button.fill5',
    hover: 'button.fill3',
    primary: 'button.fill3',
    secondary: 'text3',
  },
  option: {
    active: 'background1',
    disabled: 'button.fill5',
    hover: 'background1',
    primary: 'background1',
    secondary: 'text2',
  },
  primary: {
    active: 'button.pressed1',
    disabled: 'button.fill5',
    hover: 'button.hover1',
    primary: 'button.fill1',
    secondary: 'text2',
  },
  secondary: {
    active: 'button.pressed2',
    disabled: 'button.fill5',
    hover: 'button.hover2',
    primary: 'button.fill2',
    secondary: 'text2',
  },
  ['secondary-light']: {
    active: 'button.pressed2',
    disabled: 'button.fill5',
    hover: 'button.fill5',
    primary: 'button.fill5',
    secondary: 'text5',
  },
});

export const sizeScheme: Record<Size, number> = {
  l: 50,
  m: 40,
  s: 30,
  xl: 60,
  xs: 25,
};

const contained = css``;

const containedText = css`
  color: ${cScheme.getColor('secondary')};
`;

const text = css`
  border: none;
  background-color: transparent;

  ${platform.isWeb &&
  css<StyledProps>`
    &:active,
    &:hover,
    &:disabled {
      background-color: transparent;
    }
  `}
`;

const textText = css`
  color: ${getColor('text1')};
`;

const outlined = css`
  border-width: 2px;
  border-style: solid;
  background-color: transparent;
  color: ${cScheme.getColor('primary')};

  ${platform.isWeb &&
  css<StyledProps>`
    &:active,
    &:hover {
      color: ${cScheme.getColor('primary')};
    }

    &:active,
    &:hover,
    &:disabled {
      background-color: transparent;
    }

    &:disabled {
      color: ${cScheme.getColor('disabled')};
    }
  `}
`;

const outlinedText = css`
  color: ${cScheme.getColor('primary')};

  ${platform.isWeb &&
  css<StyledProps>`
    &:active,
    &:hover {
      color: ${cScheme.getColor('primary')};
    }

    &:disabled {
      color: ${cScheme.getColor('disabled')};
    }
  `}
`;

const baseText = css<Required<StyledProps>>`
  color: ${cScheme.getColor('secondary')};

  ${p => {
    switch (p.variant) {
      case 'contained':
        return containedText;
      case 'outlined':
        return outlinedText;
      case 'text':
        return textText;
    }
  }}

  ${p => {
    switch (p.size) {
      case 'l':
        return css`
          font-size: ${ms(16)};
        `;
      case 'm':
        return css`
          font-size: ${ms(15)};
        `;
      case 's':
        return css`
          font-size: ${ms(14)};
        `;
      case 'xl':
        return css`
          font-size: ${ms(18)};
        `;
      case 'xs':
        return css`
          font-size: ${ms(13)};
        `;
    }
  }};
`;

/**
 * Basic shared css for any renderer.
 */
const base = css<Required<StyledProps>>`
  box-sizing: border-box;
  border-radius: ${ms(10)};
  outline: none;
  border: none;
  padding: 0 ${ms(15)};
  background-color: ${cScheme.getColor('primary')};
  border-color: ${cScheme.getColor('primary')};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all ease 0.2s;
  user-select: none;

  ${platform.isWeb &&
  css<StyledProps>`
    -webkit-touch-callout: none;
    -webkit-tap-highlight-color: rgba(255, 255, 255, 0);
  `}

  ${platform.isWeb &&
  css<StyledProps>`
    &:hover {
      cursor: pointer;
      background-color: ${cScheme.getColor('hover')};
      border-color: ${cScheme.getColor('hover')};
    }

    &:active {
      background-color: ${cScheme.getColor('active')};
      border-color: ${cScheme.getColor('active')};
    }

    &:disabled,
    &:disabled:hover {
      cursor: not-allowed;

      ${p =>
        p.disabled &&
        p.scheme === 'secondary-light' &&
        css`
          opacity: 1;
          pointer-events: none;
        `};
    }
  `}

  ${p =>
    p.disabled &&
    css`
      opacity: 0.4;
      cursor: not-allowed;
    `};

  ${p => {
    switch (p.size) {
      case 'l':
        return css`
          height: ${ms(sizeScheme.l)};
        `;
      case 'm':
        return css`
          height: ${ms(sizeScheme.m)};
        `;
      case 's':
        return css`
          height: ${ms(sizeScheme.s)};
        `;
      case 'xl':
        return css`
          height: ${ms(sizeScheme.xl)};
          padding: 0 ${ms(20)};
        `;
      case 'xs':
        return css`
          height: ${ms(sizeScheme.xs)};
          border-radius: ${ms(4)};
        `;
    }
  }}

  ${p => {
    switch (p.variant) {
      case 'contained':
        return contained;
      case 'outlined':
        return outlined;
      case 'text':
        return text;
    }
  }}
`;

export const web = css<StyledProps>`
  ${base};
  ${baseText};
  display: inline-flex;
`;

export const native = css<StyledProps>`
  ${base};
`;

export const nativeText = css<StyledProps>`
  font-family: ${p => p.theme.font.regular};
  text-align: center;
  ${baseText}
`;

export const nativeContentContainer = css`
  display: flex;
  flex-direction: row;
  align-items: center;
`;
